import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaChevronRight, FaChevronUp, FaComments, FaPaperPlane, FaSpinner } from "react-icons/fa";
import BrokerAdminLabel from "./BrokerAdminLabel";
import {
  BROKER_APPLICATION_NOTE_RECORD_TYPE,
  BrokerService,
  getBrokerProfile,
} from "../../pages/Broker/BrokerService";

const NOTE_ROWS = 25;

const getRows = (response) => {
  const data = response?.data || {};
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
};

const getTotal = (response, fallback) => {
  const total = Number(response?.data?.total);
  return Number.isFinite(total) ? total : fallback;
};

const formatTime = (value) => {
  if (!value) return "Not available";
  const timestamp = Number(value);
  const date = Number.isFinite(timestamp) ? new Date(timestamp * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRemark = (note = {}) => {
  const content = note.content || {};
  return String(content.remark || content.title || "").trim();
};

const BrokerNoteAuthor = ({ note, currentAdminId }) => {
  if (note?.byType === 6 || String(note?.by || "").toLowerCase() === "system") {
    return <span className="broker-note__author-text">System</span>;
  }

  // byType 5 = FV Bank staff; broker admins are byType 7 (resolved by name below).
  if (note?.byType === 5) {
    return <span className="broker-note__author-text">FV Bank team</span>;
  }

  if (note?.by && note.by === currentAdminId) {
    return <span className="broker-note__author-text">You</span>;
  }

  if (note?.by) {
    return <BrokerAdminLabel id={note.by} fallback="FV Bank team" />;
  }

  return <span className="broker-note__author-text">FV Bank team</span>;
};

const BrokerNotesPanel = ({
  applicationId,
  recordOf,
  recordType = BROKER_APPLICATION_NOTE_RECORD_TYPE,
  title = "Notes",
  subtitle = "Shared with FV Bank",
  active = true,
  collapsible = false,
  expanded = true,
  onToggle,
  onCollapse,
}) => {
  const targetRecordOf = recordOf || applicationId;
  const profile = getBrokerProfile() || {};
  const currentAdminId = profile.brokerAdminId || profile.brokerAdmin_id || "";
  const [notes, setNotes] = useState([]);   // ascending: oldest first, newest last
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const ascending = (rows) => rows.slice().sort((left, right) => Number(left?.at || 0) - Number(right?.at || 0));
  const hasMore = notes.length < total;

  // Initial / refresh load — newest page, pinned to the bottom (chat style).
  const loadNotes = useCallback(async () => {
    if (!targetRecordOf || !active) return;
    setLoading(true);
    setError("");
    try {
      const response = await BrokerService.comments(targetRecordOf, { rows: NOTE_ROWS, page: 1, recordType });
      const rows = ascending(getRows(response));
      setNotes(rows);
      setPage(1);
      setTotal(getTotal(response, rows.length));
      stickToBottomRef.current = true;
    } catch {
      setError("Unable to load notes.");
    } finally {
      setLoading(false);
    }
  }, [active, recordType, targetRecordOf]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Pin to the bottom after the newest page renders / a new note is added.
  useEffect(() => {
    if (!listRef.current || !stickToBottomRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [notes.length]);

  // Scrolling to the top loads the previous (older) page and prepends it,
  // keeping the viewport anchored so the list grows upward.
  const loadOlder = useCallback(async () => {
    if (loadingMore || loading || notes.length >= total) return;
    const el = listRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;
    const prevScrollTop = el ? el.scrollTop : 0;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const response = await BrokerService.comments(targetRecordOf, { rows: NOTE_ROWS, page: nextPage, recordType });
      const older = ascending(getRows(response));
      if (older.length) {
        stickToBottomRef.current = false;
        setNotes((current) => [...older, ...current]);
        setPage(nextPage);
        setTotal(getTotal(response, total));
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight + prevScrollTop;
        });
      }
    } catch {
      // keep the notes already loaded
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, notes.length, total, page, targetRecordOf, recordType]);

  const handleListScroll = (event) => {
    if (event.target.scrollTop <= 48 && hasMore && !loadingMore) loadOlder();
  };

  const hasDraft = draft.trim().length > 0;

  const visibleNotes = useMemo(() => (
    notes.filter((note) => getRemark(note))
  ), [notes]);

  const saveNote = async (event) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value || !targetRecordOf || saving) return;

    setSaving(true);
    setError("");
    try {
      await BrokerService.addComment(targetRecordOf, value, recordType);
      setDraft("");
      await loadNotes();
    } catch {
      setError("Unable to save note.");
    } finally {
      setSaving(false);
    }
  };

  const isOpen = !collapsible || expanded;

  const headerInner = (
    <>
      <span className="broker-notes__icon" aria-hidden="true">
        <FaComments />
      </span>
      <div className="broker-notes__heading">
        <h3>{title}</h3>
        <p>{total ? `${total} note${total === 1 ? "" : "s"}` : subtitle}</p>
      </div>
      {collapsible ? (
        <span className="broker-notes__chevron" aria-hidden="true">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      ) : null}
    </>
  );

  return (
    <section className={`broker-notes${collapsible ? " broker-notes--collapsible" : ""}`} aria-label={title}>
      {collapsible ? (
        <button
          type="button"
          className="broker-notes__header broker-notes__header--toggle"
          onClick={() => onToggle?.()}
          aria-expanded={isOpen}
        >
          {headerInner}
        </button>
      ) : (
        <header className="broker-notes__header">
          {headerInner}
          {onCollapse ? (
            <button
              type="button"
              className="broker-notes__collapse"
              onClick={onCollapse}
              aria-label="Collapse notes"
              title="Collapse notes"
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          ) : null}
        </header>
      )}

      {isOpen ? (
      <>
      <div className="broker-notes__list" ref={listRef} onScroll={handleListScroll}>
        {loadingMore ? (
          <div className="broker-notes__state broker-notes__state--more">
            <FaSpinner className="broker-spin" aria-hidden="true" />
            <span>Loading older notes</span>
          </div>
        ) : null}

        {loading ? (
          <div className="broker-notes__state">
            <FaSpinner className="broker-spin" aria-hidden="true" />
            <span>Loading notes</span>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="broker-notes__state broker-notes__state--error">
            <span>{error}</span>
            <button type="button" onClick={loadNotes}>Retry</button>
          </div>
        ) : null}

        {!loading && !error && !visibleNotes.length ? (
          <div className="broker-notes__empty">
            <strong>No notes yet</strong>
          </div>
        ) : null}

        {!loading && !error ? visibleNotes.map((note) => (
          <article className="broker-note" key={note._id || `${note.by}-${note.at}`}>
            <div className="broker-note__meta">
              <BrokerNoteAuthor note={note} currentAdminId={currentAdminId} />
              <time>{formatTime(note.at)}</time>
            </div>
            <p>{getRemark(note)}</p>
          </article>
        )) : null}
      </div>

      <form className="broker-notes__composer" onSubmit={saveNote}>
        <label htmlFor="broker-record-note">Add note</label>
        <textarea
          id="broker-record-note"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Write a note"
          maxLength={1000}
          rows={4}
        />
        <div className="broker-notes__composer-actions">
          <span>{draft.trim().length}/1000</span>
          <button type="submit" className="broker-solid-button" disabled={!hasDraft || saving}>
            {saving ? <FaSpinner className="broker-spin" aria-hidden="true" /> : <FaPaperPlane aria-hidden="true" />}
            Add note
          </button>
        </div>
      </form>
      </>
      ) : null}
    </section>
  );
};

export default BrokerNotesPanel;
